/* eslint-disable @typescript-eslint/camelcase */
import { getAuthenticationHeader } from '@fenderdigital/custom-hooks/lib/cognito';
import fetch from '../core/fetch';
import { parseResponse } from '../core/fetch/response';
import Cookie from './cookie';
import Subscriptions from './subscriptions';
import { parseZipResponse } from '../utils/fender-connect-utils';
import AuthorizationHeader from './authorization-header';

const paymentMethods = {
  PAYMENT_APPLE: 'apple',
  PAYMENT_PAYPAL: 'PayPal',
  PAYMENT_CREDIT_CARD: 'Stripe',
};

export const errorTypes = {
  POSSIBLE_BOT: 'possible_bot',
};

const parseState = state => {
  const washingtonDC = 'Washington DC';
  if (state && state.indexOf('District of Columbia') > -1) {
    return washingtonDC;
  }
  if (state === 'DC') {
    return washingtonDC;
  }
  return state;
};

const getUTMData = () => {
  const utm = Cookie.getUtms();
  const clickref = Cookie.getClickRef();
  const affiliateId = Cookie.getAffiliateId();
  return {
    'affiliate-id': affiliateId,
    clickref,
    'utm-campaign': utm.utm_campaign,
    'utm-medium': utm.utm_medium,
    'utm-source': utm.utm_source,
  };
};

const createSubscription = async (orderData, subscriptions) => {
  const token = await getAuthenticationHeader(true);
  const url = `${process.env.REACT_APP_CLIENT_FC_SUBSCRIPTIONS_API}/subscriptions`;
  const { play } = subscriptions || {};
  let parsedOrderData = { ...orderData, action: 'renew' };
  let method = 'PATCH';
  if (!play || play === 'unsubscribed') {
    method = 'POST';
    parsedOrderData = { ...orderData };
  }
  let params = {};
  if (token) {
    params = {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(parsedOrderData),
    };
  }
  const response = await fetch(url, params);
  return response;
};

const getPayPalData = (payPalResponse, payPalDetails) => {
  const { orderID } = payPalResponse;
  const { email, id } = payPalDetails;
  return {
    city: payPalDetails['ship-to-city'],
    country: payPalDetails['country-code'],
    currency: payPalDetails['currency-code'],
    'first-name': payPalDetails['first-name'],
    'last-name': payPalDetails['last-name'],
    'paypal-ectoken': orderID,
    'paypal-baid': id,
    'paypal-email': email,
    'product-sku': process.env.REACT_APP_CLIENT_PLAY_PRODUCT_SKU,
    postal: payPalDetails['ship-to-zip'],
    state: parseState(payPalDetails['ship-to-state']),
  };
};

const getPayPalDetails = async ecToken => {
  const headers = await AuthorizationHeader.getCognitoHeader();
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/paypal/billing-agreement?token=${ecToken}`;
  const response = await fetch(url, {
    method: 'GET',
    ...headers,
  });
  return parseResponse(response);
};

const getPlayUserData = (user, country) => {
  const { email, userID } = user;
  return {
    email,
    'first-name': user['first-name'],
    'last-name': user['last-name'],
    'user-id': userID,
  };
};

const createPayPalSubscription = async (
  payPalResponse,
  user,
  subscriptions,
  selectedPlan,
  redemptionCode = null,
) => {
  try {
    const payPalDetails = await getPayPalDetails(payPalResponse.paymentToken);
    const { price } = selectedPlan.pricing;
    const utmData = getUTMData();
    const userData = getPlayUserData(user);
    const payPalData = getPayPalData(payPalResponse, payPalDetails);
    const lowerCasedCode = redemptionCode ? redemptionCode.toLowerCase() : null;
    const orderData = {
      ...utmData,
      ...userData,
      ...payPalData,
      'payment-type': paymentMethods.PAYMENT_PAYPAL,
      price,
      'product-rate-plan-sku': selectedPlan['product-rate-plan-sku'],
      'product-rate-plan-id': selectedPlan['product-rate-plan-id'] || null,
      'redemption-code': lowerCasedCode,
    };
    return createSubscription(orderData, subscriptions);
  } catch (error) {
    return Promise.resolve(error);
  }
};

const getCardData = card => {
  const { brand, country, exp_month, exp_year, last4, name } = card;
  return {
    country,
    'credit-card-expire-month': exp_month,
    'credit-card-expire-year': exp_year,
    'credit-card-holder-name': name,
    'credit-card-number': last4,
    'credit-card-type': brand,
  };
};

const getStripeCustomerToken = async id => {
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/stripe/customers`;
  const headers = await AuthorizationHeader.getCognitoHeader();
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ token: id }),
    ...headers,
  });
  if (response.status >= 200 && response.status < 300) {
    return parseResponse(response);
  }
  return Promise.reject(response);
};

const getStripeData = async (paymentToken, customerId, paymentMethodId, zip) => {
  const { token } = paymentToken;
  const { card } = token;
  const cardData = getCardData(card);
  return {
    ...cardData,
    postal: zip,
    'stripe-card-token-id': paymentMethodId,
    'stripe-customer-token-id': customerId,
  };
};

const getPlanData = plan => {
  const { pricing } = plan;
  const { currency, price } = pricing;
  return {
    currency,
    price,
    'product-rate-plan-sku': plan['product-rate-plan-sku'],
  };
};

const roundToDecimal = (value, exp) => {
  // Modified example from:
  // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/ceil#decimal_adjustment

  // If the exp is undefined or zero...
  if (typeof exp === 'undefined' || +exp === 0) {
    return Math.round(value);
  }
  value = +value;
  exp = +exp;
  // If the value is not a number or the exp is not an integer...
  if (isNaN(value) || !(typeof exp === 'number' && exp % 1 === 0)) {
    return NaN;
  }
  /* eslint-disable prefer-template */
  // Shift
  value = value.toString().split('e');
  value = Math.round(+(value[0] + 'e' + (value[1] ? +value[1] - exp : -exp)));
  // Shift back
  value = value.toString().split('e');
  return +(value[0] + 'e' + (value[1] ? +value[1] + exp : exp));
  /* eslint-enable prefer-template */
};

const getPromoDiscount = (promoCode, price) => {
  const { message } = promoCode || {};
  if (message !== 'valid') {
    return 0;
  }
  const percentage = promoCode['discount-percentage'] * 0.01;
  const rawDiscount = parseFloat(percentage * price).toFixed(3);
  return roundToDecimal(rawDiscount, -2).toFixed(2);
};

const getPricePerMonth = price => {
  if (isNaN(price)) {
    return '';
  }
  return roundToDecimal(price / 12, -2).toFixed(2);
};

const getState = async ({ postal, country }) => {
  const zipResponse = await Subscriptions.getCityState(postal, country);
  if (zipResponse && zipResponse.status >= 200 && zipResponse.status < 300) {
    if (country === 'US' || country === 'CA') {
      const zipResponseData = await zipResponse.json();
      const { state } = parseZipResponse(zipResponseData) || {};
      const defaultState = country === 'CA' ? 'Ontario' : 'Florida';
      return state || defaultState;
    }
    // returns null for non-US/CA users
    return null;
  }
  const error = new Error(JSON.stringify(zipResponse));
  error.code = zipResponse.status;
  throw error;
};

const formatStripeOrder = async (
  paymentToken,
  customerId,
  paymentMethodId,
  user,
  zip,
  plan,
  redemptionCode = '',
) => {
  const stripeData = await getStripeData(paymentToken, customerId, paymentMethodId, zip);
  const userData = getPlayUserData(user, stripeData.country);
  const planData = getPlanData(plan);
  const utmData = getUTMData();
  const state = await getState(stripeData);
  const lowerCasedCode = redemptionCode ? redemptionCode.toLowerCase() : '';
  return {
    ...stripeData,
    ...userData,
    ...planData,
    ...utmData,
    state: parseState(state),
    'payment-type': paymentMethods.PAYMENT_CREDIT_CARD,
    'product-sku': process.env.REACT_APP_CLIENT_PLAY_PRODUCT_SKU,
    'redemption-code': lowerCasedCode,
  };
};

const getStripeClientSecret = async ({ recaptchaToken, recaptchaTokenVersion }) => {
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/stripe/setupintent`;
  const headers = await AuthorizationHeader.getCognitoHeader();
  const body = {
    recaptcha_token: recaptchaToken,
    recaptcha_version: recaptchaTokenVersion,
  };
  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
    ...headers,
  });
  if (response.status >= 200 && response.status < 300) {
    return parseResponse(response);
  }
  return Promise.reject(response);
};

export const confirmStripeCard = async ({
  paymentTokenId,
  name,
  stripe,
  recaptchaToken,
  recaptchaTokenVersion,
}) => {
  const { client_secret, is_suspicious, customer_id } = await getStripeClientSecret({
    recaptchaToken,
    recaptchaTokenVersion,
  });
  if (is_suspicious) {
    throw errorTypes.POSSIBLE_BOT;
  }
  const result = await stripe.confirmCardSetup(client_secret, {
    payment_method: {
      card: {
        token: paymentTokenId,
      },
      billing_details: { name },
    },
  });
  if (result.error) throw result.error;
  return { customerId: customer_id, paymentMethodId: result.setupIntent.payment_method };
};

const getPayPalToken = async () => {
  const headers = await AuthorizationHeader.getCognitoHeader();
  const url = `${process.env.REACT_APP_CLIENT_PAYMENT_HELPERS_API}/paypal/express-checkout-token`;
  const response = await fetch(url, {
    method: 'GET',
    ...headers,
  });
  return parseResponse(response);
};

export {
  createPayPalSubscription,
  createSubscription,
  formatStripeOrder,
  getCardData,
  getPayPalData,
  getPayPalDetails,
  getPayPalToken,
  getPromoDiscount,
  getPricePerMonth,
  getStripeCustomerToken,
  getUTMData,
  parseState,
};
export default paymentMethods;
